import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from 'antd';

import { useLiteratureSearch } from '../../../../../hooks/useResearch';
import { formatInZone } from '../../../../../utils/time';
import { AiPanelReport, AiPanelTrait } from '../../../../../types/geneCodes/AiPanelReportTypes';
import ReferenceList from './ReferenceList';
import ReportNav, { prefersReducedMotion } from './ReportNav';
import TraitCard from './TraitCard';
import { RecordList } from './RecordList';
import { normalizeTrait } from './normalizeTrait';
import { scrollParentOf, useScrollSpy } from './useScrollSpy';
import {
	ADVANTAGE,
	AVOID,
	BIOHACK,
	ClinicalConsiderations,
	CognitiveStrengthsSection,
	DIET,
	EXERCISE,
	FURTHER_READING,
	InjuryRiskSection,
	LIFESTYLE,
	PHARMACEUTICAL,
	PerformanceOptimizationSection,
	PersonalityDimensionsSection,
	PowerVsEnduranceSection,
	RecoveryProfileSection,
} from './sections';
import {
	CITATION_QUERY_SIZE,
	buildCitationQuery,
	collectCitations,
	verifyCitations,
} from './citations';
import { BulletList, DualShapeList, Html, SubHeading, hasItems, text } from './primitives';
import { sortTraitsByImpact } from './severity';
import './panelReport.css';

export { getSeverityColor } from './severity';

// The legacy coloured each section's cards from a fixed palette — avoid red, pharmaceutical
// amber, biohacking purple, strengths green, biomarkers blue (genetic-report.css:280-460). The
// tone carries that mapping onto tokens; `tinted` marks the three sections the legacy painted
// as a whole coloured box because they hold prose and no cards.
type ReportTone = 'info' | 'danger' | 'warning' | 'success' | 'insight';

interface ReportSection {
	id: string;
	title: string;
	nav?: string;
	count?: number;
	meta?: string;
	tone?: ReportTone;
	tinted?: boolean;
	children: React.ReactNode;
}

const CITATIONS_UNAVAILABLE = 'Citation verification is unavailable, so references are not shown.';

// Matches the nav's resting height in panelReport.css; replaced by the measured value on mount.
const NAV_HEIGHT = 44;

// A jump parks a section GAP below the nav, so the spy line has to sit lower than that or the
// section it just scrolled to reads as not-yet-reached and the previous pill stays lit.
const JUMP_GAP = 8;
const SPY_SLACK = 8;

const PanelReport: React.FC<{ data: AiPanelReport; generatedAt?: string | null }> = ({ data, generatedAt }) => {
	const citations = useMemo(() => collectCitations(data.references), [data.references]);
	const query = useMemo(() => buildCitationQuery(citations), [citations]);

	// ONE literature request per report, whatever the citation count — never one per citation.
	const literature = useLiteratureSearch(
		{ q: query, size: CITATION_QUERY_SIZE, record: false },
		citations.length > 0 && query.length > 0,
	);
	const literatureData = literature.data;
	const literatureFailed = Boolean(literature.isError);
	const settled = literatureFailed || literatureData !== undefined;

	const verified = useMemo(() => {
		if (!literatureData || literatureData.available === false) return [];
		return verifyCitations(citations, literatureData.data);
	}, [citations, literatureData]);

	// Measures the false-negative rate of the verification pass. Ids and counts only.
	useEffect(() => {
		if (citations.length === 0 || !settled || verified.length === citations.length) return;
		console.warn('gene_codes.panel_report.citations.dropped', {
			total: citations.length,
			verified: verified.length,
		});
	}, [citations.length, verified.length, settled]);

	const effects = data.effects_of_gene_variants;
	const recommendations = data.actionable_recommendations;
	const clinical = data.clinical_considerations;
	const biohacking = data.performance_biohacking;
	const strengths = data.strengths_benefits;

	// Repairs the ~25 drifted key shapes in already-stored rows, then ranks by clinical impact.
	const traits = useMemo(
		() => sortTraitsByImpact((Array.isArray(effects?.traits) ? effects.traits : []).map(normalizeTrait)),
		[effects],
	);

	const sections: ReportSection[] = [];
	const push = (section: ReportSection) => sections.push(section);

	if (text(data.introduction)) {
		push({ id: 'introduction', title: 'Introduction', children: <Html value={data.introduction} /> });
	}

	if (text(data.key_findings)) {
		// The summary of the whole report; the tinted surface is what separates it from the body.
		push({ id: 'key_findings', title: 'Key findings', tone: 'info', tinted: true, children: <Html value={data.key_findings} /> });
	}

	if (effects && (text(effects.summary) || traits.length > 0)) {
		push({
			id: 'effects',
			title: 'Effects of gene variants',
			nav: 'Gene variants',
			count: traits.length,
			children: (
				<>
					<Html value={effects.summary} />
					{/* DualShapeList, not a bare map — legacy rows store traits as flat strings. */}
					<DualShapeList<AiPanelTrait>
						items={traits}
						render={(trait, index) => <TraitCard key={index} trait={trait} />}
					/>
				</>
			),
		});
	}

	if (text(data.integrative_interpretation)) {
		push({
			id: 'integrative',
			title: 'Integrative interpretation',
			nav: 'Interpretation',
			children: <Html value={data.integrative_interpretation} />,
		});
	}

	if (recommendations && (hasItems(recommendations.diet) || hasItems(recommendations.exercise) || hasItems(recommendations.lifestyle))) {
		push({
			id: 'recommendations',
			title: 'Actionable recommendations',
			nav: 'Recommendations',
			children: (
				<>
					{hasItems(recommendations.diet) && (
						<>
							<SubHeading>Diet</SubHeading>
							<RecordList items={recommendations.diet} spec={DIET} />
						</>
					)}
					{hasItems(recommendations.exercise) && (
						<>
							<SubHeading>Exercise</SubHeading>
							<RecordList items={recommendations.exercise} spec={EXERCISE} />
						</>
					)}
					{hasItems(recommendations.lifestyle) && (
						<>
							<SubHeading>Lifestyle</SubHeading>
							<RecordList items={recommendations.lifestyle} spec={LIFESTYLE} />
						</>
					)}
				</>
			),
		});
	}

	if (hasItems(data.things_to_avoid)) {
		push({
			id: 'avoid',
			title: 'Things to avoid',
			tone: 'danger',
			count: data.things_to_avoid?.length,
			children: <RecordList items={data.things_to_avoid} spec={AVOID} />,
		});
	}

	if (clinical && (text(clinical.summary) || hasItems(clinical.biomarkers) || hasItems(clinical.screening_recommendations) || hasItems(clinical.doctor_discussion_points))) {
		push({
			id: 'clinical',
			title: 'Clinical considerations',
			nav: 'Clinical',
			tone: 'info',
			children: <ClinicalConsiderations value={clinical as never} />,
		});
	}

	if (hasItems(data.pharmaceutical_interactions)) {
		push({
			id: 'pharmaceutical',
			title: 'Pharmaceutical interactions',
			nav: 'Pharmaceutical',
			tone: 'warning',
			count: data.pharmaceutical_interactions?.length,
			children: <RecordList items={data.pharmaceutical_interactions} spec={PHARMACEUTICAL} />,
		});
	}

	if (biohacking && (text(biohacking.disclaimer) || hasItems(biohacking.strategies))) {
		push({
			id: 'biohacking',
			title: 'Performance biohacking',
			nav: 'Biohacking',
			tone: 'insight',
			children: (
				<>
					<Html value={biohacking.disclaimer} />
					<RecordList items={biohacking.strategies} spec={BIOHACK} />
				</>
			),
		});
	}

	if (text(data.evolutionary_perspective)) {
		push({
			id: 'evolutionary',
			title: 'Evolutionary perspective',
			nav: 'Evolutionary',
			tone: 'insight',
			tinted: true,
			children: <Html value={data.evolutionary_perspective} />,
		});
	}

	if (strengths && (text(strengths.summary) || hasItems(strengths.specific_advantages))) {
		push({
			id: 'strengths',
			title: 'Genetic strengths & benefits',
			nav: 'Strengths',
			tone: 'success',
			children: (
				<>
					<Html value={strengths.summary} />
					<RecordList items={strengths.specific_advantages} spec={ADVANTAGE} />
				</>
			),
		});
	}

	if (text(data.concluding_statement)) {
		push({ id: 'conclusion', title: 'Conclusion', children: <Html value={data.concluding_statement} /> });
	}

	if (hasItems(data.further_reading)) {
		// Books are not in the PubMed corpus, so verifying them there would delete every one of
		// them. They render unverified and the section note says so.
		push({
			id: 'further_reading',
			title: 'Further reading',
			meta: 'Unverified — books are not checked against PubMed.',
			count: data.further_reading?.length,
			children: <RecordList items={data.further_reading} spec={FURTHER_READING} />,
		});
	}

	if (citations.length > 0) {
		if (literatureFailed || (literatureData && literatureData.available === false)) {
			const message =
				literatureData && literatureData.available === false && text(literatureData.message)
					? (text(literatureData.message) as string)
					: CITATIONS_UNAVAILABLE;
			push({
				id: 'references',
				title: 'References',
				meta: 'Unavailable.',
				children: <Alert type="warning" showIcon message={message} className="mg-citations-alert" />,
			});
		} else if (verified.length > 0) {
			push({
				id: 'references',
				title: 'References',
				meta: `${verified.length} verified against PubMed.`,
				count: verified.length,
				children: <ReferenceList citations={verified} />,
			});
		}
	}

	if (hasItems(data.disclaimers)) {
		push({
			id: 'disclaimers',
			title: 'Important disclaimers',
			nav: 'Disclaimers',
			tone: 'warning',
			tinted: true,
			children: <BulletList items={data.disclaimers} />,
		});
	}

	if (data.personality_dimensions && typeof data.personality_dimensions === 'object') {
		push({ id: 'personality', title: 'Personality dimensions', nav: 'Personality', children: <PersonalityDimensionsSection value={data.personality_dimensions} /> });
	}
	if (data.cognitive_strengths_opportunities && typeof data.cognitive_strengths_opportunities === 'object') {
		push({ id: 'cognitive', title: 'Cognitive strengths & opportunities', nav: 'Cognitive', children: <CognitiveStrengthsSection value={data.cognitive_strengths_opportunities} /> });
	}
	if (data.power_vs_endurance && typeof data.power_vs_endurance === 'object') {
		push({ id: 'power_vs_endurance', title: 'Power vs endurance', nav: 'Power vs endurance', children: <PowerVsEnduranceSection value={data.power_vs_endurance} /> });
	}
	if (data.injury_risk && typeof data.injury_risk === 'object') {
		push({ id: 'injury_risk', title: 'Injury risk', children: <InjuryRiskSection value={data.injury_risk} /> });
	}
	if (data.recovery_profile && typeof data.recovery_profile === 'object') {
		push({ id: 'recovery_profile', title: 'Recovery profile', nav: 'Recovery', children: <RecoveryProfileSection value={data.recovery_profile} /> });
	}
	if (data.performance_optimization && typeof data.performance_optimization === 'object') {
		push({ id: 'performance_optimization', title: 'Performance optimization', nav: 'Optimization', children: <PerformanceOptimizationSection value={data.performance_optimization} /> });
	}

	// ⚠️ RENDERED FROM THE TRAITS ON SCREEN, NEVER FROM `report_metadata`'s OWN COUNTS.
	// `xoe-agent` computes that block now, but 3060 legacy rows and 94 pre-fix rows still
	// carry the model's fabricated version — 93 of 94 said `2024-12-19`, and the count
	// disagreed with the array in 40 of 94. Only the panel name is read from the payload;
	// `generatedAt` is the stored row's own timestamp, not anything the model wrote.
	const panelName = text(data.report_metadata?.panel_type) ?? text(data.panel_id);
	const withRiskAlleles = traits.filter(
		(trait) => typeof trait === 'object' && trait !== null && Number(trait.risk_allele_count) > 0,
	).length;
	const generatedLabel = formatInZone(generatedAt, 'D MMM YYYY');

	const rootRef = useRef<HTMLDivElement>(null);
	const [navHeight, setNavHeight] = useState(NAV_HEIGHT);
	const idKey = sections.map((section) => section.id).join('|');
	const navIds = useMemo(() => (idKey ? idKey.split('|') : []), [idKey]);
	const { activeId, pin } = useScrollSpy(rootRef, navIds, navHeight + JUMP_GAP + SPY_SLACK);

	const jumpTo = useCallback(
		(id: string) => {
			const root = rootRef.current;
			const target = root?.querySelector<HTMLElement>(`[data-report-section="${id}"]`);
			if (!root || !target) return;
			pin(id);
			const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
			const scroller = scrollParentOf(root);
			if (!scroller || typeof scroller.scrollTo !== 'function') {
				target.scrollIntoView({ behavior, block: 'start' });
				return;
			}
			const top =
				scroller.scrollTop + target.getBoundingClientRect().top - scroller.getBoundingClientRect().top - navHeight - JUMP_GAP;
			scroller.scrollTo({ top: Math.max(top, 0), behavior });
		},
		[navHeight, pin],
	);

	return (
		<div className="mg-panel-report" ref={rootRef} style={{ '--mg-report-nav-h': `${navHeight}px` } as React.CSSProperties}>
			<header className="mg-report-header">
				<div className="mg-report-header__eyebrow">Panel report</div>
				<h2 className="mg-report-header__title">{panelName}</h2>
				<div className="mg-report-header__meta">
					<span>
						<b>{traits.length}</b> traits analysed
					</span>
					<span>
						<b>{withRiskAlleles}</b> with risk alleles
					</span>
					{generatedLabel && <span>Generated {generatedLabel}</span>}
				</div>
			</header>

			{sections.length > 1 && (
				<ReportNav
					sections={sections.map((section) => ({ id: section.id, label: section.nav ?? section.title }))}
					activeId={activeId}
					onSelect={jumpTo}
					onHeightChange={setNavHeight}
				/>
			)}

			{sections.map((section) => (
				<section
					key={section.id}
					data-report-section={section.id}
					aria-labelledby={`mg-report-${section.id}`}
					className={[
						'mg-report-section',
						section.tone ? `mg-report-section--${section.tone}` : '',
						section.tinted ? 'mg-report-section--tinted' : '',
					]
						.filter(Boolean)
						.join(' ')}
				>
					<div className="mg-report-section__head">
						<h3 id={`mg-report-${section.id}`} className="mg-report-section__title">
							{section.title}
							{section.count !== undefined && section.count > 0 && (
								<span className="mg-report-section__count">{section.count}</span>
							)}
						</h3>
						{section.meta && <div className="mg-report-section__meta">{section.meta}</div>}
					</div>
					<div className="mg-report-section__body">{section.children}</div>
				</section>
			))}
		</div>
	);
};

export default PanelReport;
